import React, { useEffect, useRef, useState } from 'react';

import { TransitionProps, TransitionState } from './Transition.types';

export const forceReflow = (node: HTMLElement) => node.offsetHeight;

/**
 * The Transition component manages the transition states ('entering', 'entered', 'exiting', 'exited')
 * of a single child component or HTML element based on the `in` prop.
 */
export const Transition = ({
  in: inProp,
  nodeRef,
  timeout = 300,
  mountOnEnter = false,
  unmountOnExit = false,
  appear = false,
  enter = true,
  exit = true,
  onEnter,
  onEntering,
  onEntered,
  onExit,
  onExiting,
  onExited,
  children,
}: TransitionProps) => {
  const [status, setStatus] = useState<TransitionState>(
    unmountOnExit || mountOnEnter ? 'unmounted' : 'exited'
  );

  const nextCallbackRef = useRef<(() => void) | null>(null);

  const cancelNextCallback = () => {
    if (nextCallbackRef.current) {
      nextCallbackRef.current();
      nextCallbackRef.current = null;
    }
  };

  const safeSetStatus = (
    nextStatus: TransitionState,
    callback?: () => void
  ) => {
    setStatus(nextStatus);
    nextCallbackRef.current = callback || null;
  };

  useEffect(() => {
    if (inProp) {
      if (status !== 'entering' && status !== 'entered') {
        if (mountOnEnter && status === 'unmounted') {
          setStatus('exited');
        }

        const node = nodeRef.current;
        if (enter && node) {
          onEnter?.(node, appear);
          forceReflow(node);

          safeSetStatus('entering', () => {
            onEntering?.(node, appear);
            const timeoutDuration =
              typeof timeout === 'number' ? timeout : timeout.enter ?? 0;

            setTimeout(() => {
              safeSetStatus('entered', () => {
                onEntered?.(node, appear);
              });
            }, timeoutDuration);
          });
        } else {
          safeSetStatus('entered');
        }
      }
    } else {
      if (status === 'entering' || status === 'entered') {
        const node = nodeRef.current;
        if (exit && node) {
          onExit?.(node);

          safeSetStatus('exiting', () => {
            onExiting?.(node);
            const timeoutDuration =
              typeof timeout === 'number' ? timeout : timeout.exit ?? 0;

            setTimeout(() => {
              safeSetStatus('exited', () => {
                onExited?.(node);
                if (unmountOnExit) {
                  setStatus('unmounted');
                }
              });
            }, timeoutDuration);
          });
        } else {
          safeSetStatus('exited');
          if (unmountOnExit) {
            setStatus('unmounted');
          }
        }
      }
    }

    return () => {
      cancelNextCallback();
    };
  }, [
    appear,
    enter,
    exit,
    inProp,
    mountOnEnter,
    nodeRef,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    status,
    timeout,
    unmountOnExit,
  ]);

  if (status === 'unmounted') {
    return null;
  }

  return <>{children(status)}</>;
};
